import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  RefreshCw, 
  Filter,
  MessageCircle,
  Clock,
  Calendar,
  FileText,
  Loader2,
  Server,
  Tag
} from 'lucide-react';
import { toast } from 'react-toastify';
import BitrixTimelineCard from '../components/BitrixTimelineCard';
import axios from 'axios';
import { BACKEND_CONFIG } from '../config/backend';
import { useAuth } from '../hooks/useAuth';

const BitrixTimelinePage = () => {
  const { getValidToken } = useAuth();
  const [dealId, setDealId] = useState('');
  const [bitrixInstance, setBitrixInstance] = useState('new'); // 'old' ou 'new'
  const [timelineItems, setTimelineItems] = useState([]);
  const [dealUfCrmFields, setDealUfCrmFields] = useState({}); // Campos UF_CRM do deal separados
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('all'); // all, comment, task, call, email
  const [activeTab, setActiveTab] = useState('activity'); // activity, comment, task, etc

  const API_URL = BACKEND_CONFIG.BASE_URL;

  // Buscar timeline do deal
  const fetchTimeline = async () => {
    if (!dealId.trim()) {
      toast.error('Por favor, informe o ID do Deal');
      return;
    }

    setLoading(true);
    try {
      // Obter token de autenticação válido
      const token = await getValidToken();
      if (!token) {
        toast.error('Sessão expirada. Por favor, faça login novamente.');
        setLoading(false);
        return;
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await axios.get(`${API_URL}/api/bitrix/timeline/${dealId}?instance=${bitrixInstance}`, {
        headers
      });
      
      if (response.data.success) {
        const items = response.data.timeline || [];
        const ufCrmFields = response.data.dealUfCrmFields || {};
        
        // Separar: timeline items sem campos UF_CRM do deal
        setTimelineItems(items);
        // Campos UF_CRM do deal em estado separado
        setDealUfCrmFields(ufCrmFields);
        
        const ufCrmCount = Object.keys(ufCrmFields).length;
        toast.success(`${items.length} itens encontrados na timeline${ufCrmCount > 0 ? ` e ${ufCrmCount} campos UF_CRM do deal` : ''}`);
      } else {
        toast.error(response.data.message || 'Erro ao buscar timeline');
        setTimelineItems([]);
        setDealUfCrmFields({});
      }
    } catch (error) {
      console.error('Erro ao buscar timeline:', error);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Por favor, faça login novamente.');
      } else if (error.response?.status === 403) {
        toast.error('Você não tem permissão para acessar esta funcionalidade.');
      } else {
        toast.error(error.response?.data?.message || 'Erro ao buscar timeline do deal');
      }
      setTimelineItems([]);
      setDealUfCrmFields({});
    } finally {
      setLoading(false);
    }
  };

  // Filtrar itens por tipo
  const filteredItems = timelineItems.filter(item => {
    if (filterType === 'all') return true;
    
    const typeId = item.TYPE_ID?.toString();
    switch (filterType) {
      case 'comment':
        return typeId === '4';
      case 'task':
        return typeId === '1';
      case 'call':
        return typeId === '2';
      case 'email':
        return typeId === '3';
      default:
        return true;
    }
  });

  // Ordenar por data (mais recente primeiro)
  const sortedItems = [...filteredItems].sort((a, b) => {
    const dateA = new Date(a.CREATED || a.CREATED_TIME || 0);
    const dateB = new Date(b.CREATED || b.CREATED_TIME || 0);
    return dateB - dateA;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Cabeçalho */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Timeline do Bitrix
          </h1>
          <p className="text-gray-600">
            Visualize atividades, comentários e tarefas de um deal
          </p>
        </motion.div>

        {/* Barra de busca */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6"
        >
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instância do Bitrix
              </label>
              <div className="relative">
                <Server className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  value={bitrixInstance}
                  onChange={(e) => setBitrixInstance(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white appearance-none cursor-pointer"
                >
                  <option value="new">Bitrix Novo (grupovillela.bitrix24.com.br)</option>
                  <option value="old">Bitrix Antigo (villela.bitrix24.com.br)</option>
                </select>
              </div>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID do Deal
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={dealId}
                  onChange={(e) => setDealId(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && fetchTimeline()}
                  placeholder="Digite o ID do deal (ex: 1458836)"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={fetchTimeline}
                disabled={loading || !dealId.trim()}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center space-x-2 shadow-lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Buscando...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5" />
                    <span>Buscar Timeline</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>

        {/* Filtros */}
        {timelineItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6"
          >
            <div className="flex items-center space-x-4">
              <Filter className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtrar por:</span>
              
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'all', label: 'Todos', icon: Calendar },
                  { value: 'comment', label: 'Comentários', icon: MessageCircle },
                  { value: 'task', label: 'Tarefas', icon: Clock },
                  { value: 'call', label: 'Chamadas', icon: Calendar },
                  { value: 'email', label: 'Emails', icon: FileText }
                ].map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    onClick={() => setFilterType(value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 ${
                      filterType === value
                        ? 'bg-blue-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{label}</span>
                  </button>
                ))}
              </div>

              <div className="ml-auto text-sm text-gray-600">
                {filteredItems.length} de {timelineItems.length} itens
              </div>
            </div>
          </motion.div>
        )}

        {/* Campos UF_CRM do Deal - Seção Separada */}
        {Object.keys(dealUfCrmFields).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-xl shadow-sm border border-orange-200 p-6 mb-6"
          >
            <div className="mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                <Tag className="w-6 h-6 text-orange-500" />
                <span>Campos Personalizados do Deal (UF_CRM)</span>
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {Object.keys(dealUfCrmFields).length} campos encontrados
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
              {Object.entries(dealUfCrmFields).map(([key, value]) => {
                const getFieldLabel = (fieldKey) => {
                  const labels = {
                    'UF_CRM_1559168142': '📊 Resumo de Vendas (Completo)',
                    'UF_CRM_1746406184': '📊 Resumo da Venda',
                    'UF_CRM_1561394928': '🖥️ Campo PC',
                    'UF_CRM_1752752091': '🏢 Franqueado',
                    'UF_CRM_1730750253': '📄 Contrato possuí franqueado',
                    'UF_CRM_DEAL_1721602576682': '🔄 Espelhamento com franqueado',
                    'UF_CRM_1731325756': '📝 Franqueado (Texto)',
                    'UF_CRM_1754058580': '🔗 Link Anexo Contrato'
                  };
                  return labels[fieldKey] || fieldKey;
                };
                
                const valueStr = typeof value === 'object' 
                  ? JSON.stringify(value, null, 2) 
                  : String(value);
                
                return (
                  <div 
                    key={key}
                    className="p-3 bg-orange-50 rounded border border-orange-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center space-x-2 mb-1">
                      <Tag className="w-3 h-3 text-orange-600 flex-shrink-0" />
                      <span className="text-xs font-semibold text-orange-900 break-words">
                        {getFieldLabel(key)}
                      </span>
                    </div>
                    <div className="text-xs text-orange-800 break-words whitespace-pre-wrap max-h-32 overflow-y-auto">
                      {valueStr.length > 200 ? `${valueStr.substring(0, 200)}...` : valueStr}
                    </div>
                    <div className="mt-1 text-xs text-orange-600 font-mono truncate">
                      {key}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Timeline */}
        {loading && timelineItems.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : sortedItems.length > 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="space-y-4">
              {sortedItems.map((item, index) => (
                <BitrixTimelineCard
                  key={item.ID || index}
                  item={item}
                  index={index}
                  isLast={index === sortedItems.length - 1}
                />
              ))}
            </div>
          </motion.div>
        ) : dealId && !loading ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhum item encontrado
            </h3>
            <p className="text-gray-600">
              Não foram encontrados itens na timeline para este deal.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Busque um Deal
            </h3>
            <p className="text-gray-600">
              Digite o ID do deal acima para visualizar sua timeline.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BitrixTimelinePage;
